import React, { useContext } from 'react';
import { useTransition, animated } from 'react-spring';
import { TRANSITION_DECELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { SPEED_SLOW } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { getEasing, getDuration, getContext, isAnimationDisabled } from 'common';
import { DialogSheetContext } from './DialogSheet';
import Styled from './styles';

const TRANSITION_RIGHT = { transform: 'translateX(-100%)' };
const TRANSITION_LEFT = { transform: 'translateX(100%)' };
const TRANSITION_BOTTOM = { transform: 'translateY(100%)' };
const TRANSITION_X_ENTER = { transform: 'translateX(0%)' };
const TRANSITION_Y_ENTER = { transform: 'translateY(0%)' };

export interface DialogSheetAnimationProps extends React.ComponentPropsWithRef<'div'> {
  /**  Content of the DialogSheetAnimation */
  children: React.ReactNode;
  /** Set true to start the animation */
  isOpen: boolean;
  /** Set true to change the direction of Animation */
  RTL?: boolean;
  /** Set true, Animation starts from bottom to top */
  editMode?: boolean;
  /** Control animation config here. This uses react-spring transitions config. See https://react-spring.io for more information. */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
}

const DialogSheetAnimation:React.FC<DialogSheetAnimationProps> = ({
  isOpen,
  RTL,
  editMode,
  children,
  animationProps,
  disableAnimation: propsdisableAnimation,
  ...otherProps
}: DialogSheetAnimationProps) => getContext(({ disableAnimation }) => {
  const context = useContext(DialogSheetContext);
  const createModeTransitionFrom = RTL ? TRANSITION_RIGHT : TRANSITION_LEFT;
  const createModeTransitionLeave = RTL ? TRANSITION_RIGHT : TRANSITION_LEFT;
  const enterValue = editMode ? TRANSITION_Y_ENTER : TRANSITION_X_ENTER;
  const fromValue = editMode ? TRANSITION_BOTTOM : createModeTransitionFrom;
  const leaveValue = editMode ? TRANSITION_BOTTOM : createModeTransitionLeave;

  const defaultAnimationConfig = {
    config: {
      duration: getDuration(SPEED_SLOW),
      easing: getEasing(TRANSITION_DECELERATE)
    },
    from: fromValue,
    enter: enterValue,
    leave: leaveValue,
    onRest: () => {
      !isOpen && context.updateIsOpen(false);
    },
    immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
  };
  const transitions = useTransition(isOpen, null, animationProps || defaultAnimationConfig);
  return (
    <>
      {transitions.map(({ item, key, props: transitionStyles }) => item && (
        <Styled.AnimationContainer
          key={key}
          as={animated.div}
          style={{
            ...transitionStyles
          }}
          {...otherProps}
        >
          {children}
        </Styled.AnimationContainer>
      ))}
    </>);
});

DialogSheetAnimation.defaultProps = {
  RTL: false,
  editMode: false,
  animationProps: undefined,
  disableAnimation: undefined,
};

DialogSheetAnimation.displayName = 'DialogSheetAnimation';


export default DialogSheetAnimation;
